import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Dropdown, Tooltip } from 'antd';
import { ArrowDown, ArrowUp, GripVertical, MoreHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { moveItem, moveToEnd } from '@/lib/reorder';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  AN ORDERED LIST WITH TWO INTERACTION PATHS OVER ONE `onReorder`.
 *
 *  • POINTER — drag, via `@dnd-kit`.
 *  • KEYBOARD AND TOUCH — a labelled Move up / Move down button per row, plus
 *    Move to top / Move to bottom in the row overflow for a long list.
 *
 *  ⚠️ THE BUTTONS ARE NOT A FALLBACK, AND THEY ARE NOT OPTIONAL.
 *
 *  `pages/OrgTree/components/MoveUnitModal.tsx` refused drag outright, and its third
 *  reason applies here unchanged: drag is not keyboard accessible without a parallel
 *  path, so the explicit control has to exist anyway. What differs is that its first two
 *  reasons do not — reordering columns in a draft policy is cheap and reversible, and
 *  there IS a sibling order to express — so P2 ships both rather than neither.
 *
 *  ⚠️ AND THE BUTTONS ARE WHAT THE SUITE ASSERTS. dnd-kit's `KeyboardSensor` derives its
 *  coordinates from `getBoundingClientRect()`, which jsdom reports as all-zero, and
 *  `window.ResizeObserver` is undefined there. The drag path is exercised by hand; the
 *  button path is exercised by `ReorderableList.test.tsx`. That asymmetry is recorded in
 *  the CHANGELOG rather than implied away.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type ReorderableItem = {
  /** Stable across a reorder. dnd-kit and React both key on it. */
  id: string;
  /** Used in every control's accessible name — "Move UB Men up". */
  label: string;
  content?: ReactNode;
};

/**
 * ⚠️ [amended D4.1] TWO ROW SHAPES, BECAUSE A CARD IS NOT A LINE OF TEXT.
 *
 *   'row'   — the default and the original: a bordered strip with the reorder controls in a
 *             horizontal flex BESIDE the content, which is right for a one-line item.
 *
 *   'block' — the row is a bare block, the content takes the full measure, and the controls
 *             are handed to `renderItem` as its third argument so the consumer places them
 *             inside its own header.
 *
 * MEASURED, which is why 'block' exists at all: at 1366 the duty point stack's `<li>` was
 * 604px and the card inside it got 420 — the controls took 184px of a card that then had to
 * fit a force grid. And `min-w-0 flex-1 truncate` was applied to the whole card, so every
 * inline descendant inherited `white-space: nowrap`.
 */
export type ReorderableRowLayout = 'row' | 'block';

/**
 * What `'block'` hands the consumer to place in its own header.
 *
 * ⚠️ [amended D4.2] TWO PIECES, BECAUSE THEY GO TO OPPOSITE ENDS OF THE ROW. A duty point
 * header puts the grip beside the disclosure chevron on the left and the menu out on the
 * right past `Duplicate`; one node could only ever land in one place.
 *
 * ⚠️ AND THE MOVE PAIR IS INSIDE `menu` HERE, NOT BESIDE IT. [P2] requires a keyboard path
 * parallel to the drag, and the menu IS one — it is reachable, it is announced, and its
 * items carry the same labels the buttons did. What it is not is two more icon buttons in a
 * header that already had eight things in it.
 */
export type RowControls = {
  /** The drag handle, alone. */
  handle: ReactNode;
  /** The overflow menu: move up, move down, move to top, move to bottom. */
  menu: ReactNode;
};

export type ReorderableListProps<T extends ReorderableItem> = {
  items: readonly T[];
  onReorder: (next: T[]) => void;
  onRemove?: (item: T) => void;
  /**
   * What each row renders. Defaults to `item.content ?? item.label`.
   *
   * `controls` is the reorder cluster — drag handle, position, up, down, the overflow menu
   * and the remove button. In `'row'` layout it is placed for you and this argument is
   * `null`; in `'block'` layout it is YOURS to place, and dropping it on the floor is how a
   * list silently loses its keyboard reordering.
   */
  renderItem?: (item: T, index: number, controls: RowControls | null) => ReactNode;
  layout?: ReorderableRowLayout;
  disabled?: boolean;
  emptyText?: string;
  ariaLabel: string;
  className?: string;
  'data-testid'?: string;
};

type RowProps<T extends ReorderableItem> = {
  item: T;
  index: number;
  total: number;
  disabled: boolean;
  layout: ReorderableRowLayout;
  focusRef: React.RefObject<string | null>;
  onMove: (from: number, to: number) => void;
  onMoveToEdge: (from: number, edge: 'start' | 'end') => void;
  onRemove?: (item: T) => void;
  /** Receives the control cluster; in `'row'` layout it is passed `null`. */
  render: (controls: RowControls | null) => ReactNode;
};

const Row = <T extends ReorderableItem>({
  item,
  index,
  total,
  disabled,
  layout,
  focusRef,
  onMove,
  onMoveToEdge,
  onRemove,
  render,
}: RowProps<T>) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled,
  });

  const handleRef = useRef<HTMLButtonElement>(null);
  const upRef = useRef<HTMLButtonElement>(null);
  const downRef = useRef<HTMLButtonElement>(null);
  const isFirst = index === 0;
  const isLast = index === total - 1;

  /**
   * ⚠️ THE FOCUS BUG EVERYONE SHIPS.
   *
   * After a move the button that was clicked may now be DISABLED (the row reached an
   * end), and focus falls to `<body>` — so a keyboard user reordering a list is thrown
   * back to the top of the page mid-task. Focus is restored deterministically: the same
   * direction if it is still available, otherwise the opposite one on the same row.
   *
   * `useLayoutEffect` rather than `useEffect` so it lands before paint and the focus
   * ring never visibly jumps.
   */
  useLayoutEffect(() => {
    if (focusRef.current !== item.id) return;
    focusRef.current = null;
    /*
     * ⚠️ [amended D4.2] THE HANDLE IS THE LAST RESORT, AND `'block'` NEEDS ONE. Its move
     * buttons live in the overflow menu, which AntD unmounts on select — so both refs are
     * null there and focus would fall to `<body>`, which is the exact bug this effect was
     * written to prevent. The grip is the one control a block row always renders.
     */
    const preferred = !isFirst ? upRef.current : downRef.current;
    const fallback = !isLast ? downRef.current : upRef.current;
    (preferred ?? fallback ?? handleRef.current)?.focus();
  }, [focusRef, item.id, isFirst, isLast]);

  const moveButton = (direction: 'up' | 'down') => {
    const isUp = direction === 'up';
    const unavailable = isUp ? isFirst : isLast;
    return (
      <Button
        ref={isUp ? upRef : downRef}
        type="text"
        size="small"
        // Disabled, never hidden: a button that vanishes at the first row shifts the
        // layout of every row above it as you reorder.
        disabled={disabled || unavailable}
        aria-label={`Move ${item.label} ${direction}`}
        icon={isUp ? <ArrowUp className="size-4" aria-hidden /> : <ArrowDown className="size-4" aria-hidden />}
        onClick={() => {
          focusRef.current = item.id;
          onMove(index, isUp ? index - 1 : index + 1);
        }}
      />
    );
  };

  /*
   * ⚠️ [amended D4.1] ONE SOURCE PER CONTROL, SPLIT AT THE POINT THE CONTENT GOES.
   *
   * `'row'` interleaves — handle, position, CONTENT, then the move pair and the menu —
   * while `'block'` hands the whole cluster to the consumer. Writing the two orders as two
   * literals is what a first cut did, and it duplicated the `Dropdown` and the `Tooltip`
   * into the same file twice: dead-obvious drift, and `e2e/specs/census.spec.ts` failed on
   * it immediately because it counts overlay sites per FILE. Two fragments, used twice.
   */
  const handle = (
    <button
      /* Both: dnd-kit needs the activator, the focus effect needs a handle to fall back to. */
      ref={(node) => {
        setActivatorNodeRef(node);
        handleRef.current = node;
      }}
      type="button"
      {...attributes}
      {...listeners}
      disabled={disabled}
      // The drag handle is a real button so it is reachable and announced; the buttons
      // beside it are what actually perform the move without a pointer.
      aria-label={`Drag ${item.label} to reorder`}
      className="cursor-grab text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      <GripVertical className="size-4" aria-hidden />
    </button>
  );

  /* The row layout's position badge. `'block'` puts the index in its own title instead. */
  const leading = (
    <>
      {handle}
      <span className="w-5 shrink-0 text-right text-xs tabular-nums text-subtle">{index + 1}</span>
    </>
  );

  const menu = (
    <Dropdown
      /*
       * ⚠️ [P4-UX] `trigger` IS NOT OPTIONAL, AND ITS ABSENCE WAS A REAL DEFECT.
       *
       * AntD's default is `['hover']`. This was the ONLY Dropdown in `web/src` without an
       * explicit trigger, and the QA report — "the ⋯ on an instruction line opens nothing,
       * ever" — was right. Measured in Chromium, separating the input paths:
       *
       *     programmatic click, pointer elsewhere : 0
       *     keyboard Enter on the focused button  : 0
       *     keyboard Space on the focused button  : 0
       *     real pointer hover                    : 1
       *
       * So it was unreachable by keyboard ENTIRELY, and by any input that is not a
       * hovering pointer. A casual test disagrees with the report because
       * `locator.click()` — like a real trackpad — hovers on its way in.
       */
      trigger={['click']}
      placement="bottomRight"
      disabled={disabled}
      menu={{
        /*
         * ⚠️ [amended D4.2] THE MOVE PAIR IS IN HERE TOO, and that is what lets `'block'`
         * drop the two icon buttons without losing [P2]'s keyboard path. Same labels, same
         * `disabled` rule at each end — disabled, never hidden, so the menu does not
         * reshuffle under the pointer as a row reaches the top.
         */
        items: [
          { key: 'up', label: `Move ${item.label} up`, disabled: isFirst },
          { key: 'down', label: `Move ${item.label} down`, disabled: isLast },
          { type: 'divider' },
          { key: 'top', label: 'Move to top', disabled: isFirst },
          { key: 'bottom', label: 'Move to bottom', disabled: isLast },
        ],
        onClick: ({ key }) => {
          focusRef.current = item.id;
          if (key === 'up' || key === 'down') {
            onMove(index, key === 'up' ? index - 1 : index + 1);
            return;
          }
          onMoveToEdge(index, key === 'top' ? 'start' : 'end');
        },
      }}
    >
      {/* Long lists: "nine clicks to move item ten to position one" without a gesture. */}
      <Button
        type="text"
        size="small"
        aria-label={`More reorder actions for ${item.label}`}
        icon={<MoreHorizontal className="size-4" aria-hidden />}
      />
    </Dropdown>
  );

  const trailing = (
    <>
      {moveButton('up')}
      {moveButton('down')}
      {menu}

      {onRemove && (
        <Tooltip title={`Remove ${item.label}`}>
          <Button
            type="text"
            size="small"
            danger
            disabled={disabled}
            aria-label={`Remove ${item.label}`}
            icon={<X className="size-4" aria-hidden />}
            onClick={() => onRemove(item)}
          />
        </Tooltip>
      )}
    </>
  );

  /*
   * ⚠️ NO BORDER, NO PADDING, NO `truncate` IN `'block'`. The consumer's own header is where
   * the controls land and its own surface is the boundary; a box around it is the
   * box-in-box this layout exists to remove. The `<li>` keeps `relative` so dnd-kit's
   * transform and the drag z-index still have somewhere to land.
   */
  if (layout === 'block') {
    return (
      <li
        ref={setNodeRef}
        style={{ transform: CSS.Transform.toString(transform), transition }}
        /*
         * ⚠️ A LIFTED ROW HAS TO BE OPAQUE, AND THE FIRST CUT OF THIS BRANCH WAS NOT.
         *
         * `'row'` carries `bg-card` at rest, so dragging one was always opaque. A block row
         * is flat on the page by design — that is the point of the layout — so it paints
         * nothing, and `z-10` then lifts a TRANSPARENT box over its neighbour. dnd-kit
         * translates the active row continuously while the others shift in steps, so the two
         * overlap for the whole gesture: two duty point headers superimposed, both
         * unreadable. The surface and the ring therefore appear only WHILE dragging, which
         * leaves the resting state flat.
         *
         * ⚠️ `ring`, NOT `border` — a border would add a pixel and shift the row as it lifts.
         * No shadow: the design language has none in dark mode.
         *
         * ⚠️ NO TEST CAN SEE THIS. dnd-kit's sensors read `getBoundingClientRect()`, which
         * jsdom reports as all-zero, so the drag path is unreachable under test and this is a
         * hand-verified claim like every other drag behaviour since [P2].
         */
        className={cn('relative min-w-0', isDragging && 'z-10 rounded-md bg-card ring-2 ring-ring')}
        data-testid="reorderable-row"
      >
        {render({ handle, menu })}
      </li>
    );
  }

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5',
        isDragging && 'relative z-10 border-ring',
      )}
      data-testid="reorderable-row"
    >
      {leading}
      <div className="min-w-0 flex-1 truncate text-sm">{render(null)}</div>
      {trailing}
    </li>
  );
};

export const ReorderableList = <T extends ReorderableItem>({
  items,
  onReorder,
  onRemove,
  renderItem,
  layout = 'row',
  disabled = false,
  emptyText = 'Nothing here yet.',
  ariaLabel,
  className,
  'data-testid': testId,
}: ReorderableListProps<T>) => {
  const [announcement, setAnnouncement] = useState('');
  /** Which row should hold focus after the next commit. See `Row`'s layout effect. */
  const focusRef = useRef<string | null>(null);

  const sensors = useSensors(
    // A small activation distance so a click on a row BUTTON is not swallowed by a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (next: T[], moved: T | undefined) => {
    onReorder(next);
    if (!moved) return;
    const position = next.findIndex((item) => item.id === moved.id) + 1;
    setAnnouncement(`${moved.label} moved to position ${String(position)} of ${String(next.length)}`);
  };

  const move = (from: number, to: number) => commit(moveItem(items, from, to), items[from]);
  const moveEdge = (from: number, edge: 'start' | 'end') => commit(moveToEnd(items, from, edge), items[from]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => item.id === active.id);
    const to = items.findIndex((item) => item.id === over.id);
    if (from === -1 || to === -1) return;
    move(from, to);
  };

  if (items.length === 0) {
    return <p className={cn('text-sm text-muted-foreground', className)}>{emptyText}</p>;
  }

  return (
    /*
     * ⚠️ [amended D4] `relative` IS LOAD-BEARING, AND A BROWSER IS THE ONLY THING THAT CAN
     * SEE WHY. The `sr-only` announcer below is `position: absolute` with no positioned
     * ancestor, so its containing block was the INITIAL one — and an absolutely positioned
     * box whose containing block is the ICB is not clipped by any `overflow: hidden`
     * between it and the root. Once a list was tall enough to push it below the fold it
     * extended `documentElement.scrollHeight` by exactly its own offset, which is §8's
     * "THE WINDOW NEVER SCROLLS" broken by a one-pixel invisible paragraph.
     *
     * MEASURED at 1366×768 on the duty point stack: html grew 531px to 1299, with the
     * announcer sitting at y=1298 and every other element accounted for. jsdom performs no
     * layout, so no unit test can see this; `e2e/specs/overlays.spec.ts` found it.
     *
     * Latent since [P2] rather than introduced here — D4 merely built the first list long
     * enough to reach past the fold.
     */
    <div className={cn('relative', className)} data-testid={testId}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          {/*
            ⚠️ [amended D4.2] A GAP, NOT A DIVIDER. D4.1 gave `'block'` `divide-y border-y`
            because its rows had no boundary of their own; D4.2 gives the duty point card a
            border back, so the rule became a SECOND boundary between the same two objects —
            a hairline butted against a card edge, which is the box-in-box D4.1 removed
            wearing one pixel. Discrete objects want space between them.
          */}
          <ul className={cn('flex flex-col', layout === 'block' ? 'gap-3' : 'gap-1.5')} aria-label={ariaLabel}>
            {items.map((item, index) => (
              <Row
                key={item.id}
                item={item}
                index={index}
                total={items.length}
                disabled={disabled}
                focusRef={focusRef}
                onMove={move}
                onMoveToEdge={moveEdge}
                layout={layout}
                {...(onRemove ? { onRemove } : {})}
                render={(controls) => (renderItem ? renderItem(item, index, controls) : (item.content ?? item.label))}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {/*
        Announced to a screen reader; invisible, and never a layout shift.

        ⚠️ dnd-kit renders a `role="status"` announcer of its OWN for the drag path, so
        there are two on the page — which is correct (they describe different gestures)
        and is why this one carries a test id rather than being queried by role alone.
      */}
      <p role="status" aria-live="polite" className="sr-only" data-testid="reorder-announcement">
        {announcement}
      </p>
    </div>
  );
};
